import { QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';
import { TransportOrder, TransportOrderStatus } from '../../domain/transportOrder';
import { useAuthentication } from '../../modules/auth/AuthenticationProvider';
import { BACKEND_URL } from '../backend-url';
import { TRANSPORT_ORDERS_BULK_PATH } from '../paths';
import { GetTransportOrderDto, toDomain } from './dto/getTransportOrderDto';

type BulkChangeTransportOrderStatusRepositoryMutationType = {
    transportOrderIds: string[];
    newStatus: TransportOrderStatus;
}

const reloadTransportOrdersFeed = (queryClient: QueryClient) => {
    return queryClient.refetchQueries({ queryKey: ['transportOrders', 'feed'] });
};

export const useBulkChangeTransportOrderStatusRepository = () => {
    const { getJwtToken } = useAuthentication();
    const queryClient = useQueryClient();

    const bulkChangeTransportOrderStatus = useMutation({
        mutationFn: (variable: BulkChangeTransportOrderStatusRepositoryMutationType) =>
            bulkChangeTransportOrderStatusFunction(variable.transportOrderIds, variable.newStatus),
        onSuccess: async (changedTransportOrders) => {
            await reloadTransportOrdersFeed(queryClient);
            changedTransportOrders.forEach((changedTransportOrder) => {
                queryClient.invalidateQueries({ queryKey: ['transportOrders', { id: changedTransportOrder.id }] });
                queryClient.setQueryData(['transportOrders'],
                    (originalTransportOrders?: Array<TransportOrder>) => {
                        if (!originalTransportOrders) {
                            return;
                        }
                        return originalTransportOrders.map((originalTransportOrder) => {
                            if (originalTransportOrder.id !== changedTransportOrder.id) {
                                return originalTransportOrder;
                            }
                            return changedTransportOrder;
                        });
                    });
            });
        },
    })

    const bulkChangeTransportOrderStatusFunction = async (transportOrderIds: string[], newStatus: TransportOrderStatus) => {
        const token = await getJwtToken();

        const result = await fetch(`${BACKEND_URL}/${TRANSPORT_ORDERS_BULK_PATH}/status`, {
            method: 'PATCH',
            body: JSON.stringify({ transportOrderIds, status: newStatus }),
            headers: {
                Authorization: `Bearer ${token}`,
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        });

        const dtos = await result.json() as GetTransportOrderDto[];

        return dtos.map(dto => toDomain(dto));
    };

    return {
        bulkChangeTransportOrderStatus: (variable: BulkChangeTransportOrderStatusRepositoryMutationType) => 
            bulkChangeTransportOrderStatus.mutateAsync(variable),
    };
};
